/**
 * VB Server - Organizations API
 *
 * Organization-level operations.
 *
 * @example
 * import { listOrgs, getOrg, updateOrg } from '@/api/server/orgs';
 *
 * const orgs = await listOrgs();
 * await updateOrg(orgId, { name: 'New Name' });
 */

import { serverGet, serverPost, serverPut, serverDelete } from './client';
import type { OrgRole } from '../../v2/services/auth';

// =============================================================================
// Types
// =============================================================================

/**
 * Organization entity from vb-server
 */
export interface Organization {
  org_id: string;
  name: string;
  image?: string | null;
  default_asr_model_id?: string | null;
  default_asr_context_id?: string | null;
  /** Firebase push id of this org's legacy Firestore tenant. Super-admin only. */
  legacy_firestore_org_id?: string | null;
  status: number;
  created: string;
  modified: string;
  // Additional fields from user membership
  org_role?: OrgRole;
  send_allowed?: boolean; // deprecated: role determines access
  membership_status?: number;
}

/**
 * Input for creating an organization
 */
export interface CreateOrgInput {
  name: string;
  image?: string | null;
}

/**
 * Input for updating an organization
 */
export interface UpdateOrgInput {
  name?: string;
  image?: string | null;
  default_asr_model_id?: string | null;
  default_asr_context_id?: string | null;
  /** Firebase push id of this org's legacy Firestore tenant. Super-admin only. */
  legacy_firestore_org_id?: string | null;
}

/**
 * Organization with users (returned from create)
 */
export interface OrganizationWithUsers extends Organization {
  users: Record<
    string,
    {
      user_id: string;
      email: string;
      name?: string;
      image?: string;
      org_role: string;
      send_allowed: boolean;
    }
  >;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List all organizations the current user has access to
 *
 * @returns Array of organizations
 */
export async function listOrgs(options?: {
  includeInactive?: boolean;
  all?: boolean;
}): Promise<Organization[]> {
  const queryParams = new URLSearchParams();
  if (options?.all) queryParams.set('all', 'true');
  if (options?.includeInactive) queryParams.set('include_inactive', 'true');
  const qs = queryParams.toString();
  return serverGet<Organization[]>(`/orgs${qs ? `?${qs}` : ''}`);
}

/**
 * Create a new organization (creator becomes admin)
 *
 * @param data - Organization data
 * @returns Created organization with users
 */
export async function createOrg(data: CreateOrgInput): Promise<OrganizationWithUsers> {
  return serverPost<OrganizationWithUsers>('/orgs', data);
}

/**
 * Get a single organization by ID
 *
 * @param orgId - Organization ID
 * @returns Organization or null if not found
 */
export async function getOrg(orgId: string): Promise<Organization | null> {
  try {
    return await serverGet<Organization>(`/orgs/${orgId}`);
  } catch {
    return null;
  }
}

/**
 * Update an organization (admin only)
 *
 * @param orgId - Organization ID
 * @param data - Updated organization data
 * @returns Updated organization
 */
export async function updateOrg(orgId: string, data: UpdateOrgInput): Promise<Organization> {
  return serverPut<Organization>(`/orgs/${orgId}`, data);
}

/**
 * Delete an organization (super-admin only, soft delete)
 *
 * @param orgId - Organization ID
 */
export async function deleteOrg(orgId: string): Promise<void> {
  return serverDelete(`/orgs/${orgId}`);
}

/**
 * Restore a soft-deleted organization (super-admin only)
 *
 * @param orgId - Organization ID
 * @returns Restored organization
 */
export async function restoreOrg(orgId: string): Promise<Organization> {
  return serverPost<Organization>(`/orgs/${orgId}/restore`, {});
}

/**
 * Get organization agent entitlements (super-admin only)
 *
 * @param orgId - Organization ID
 * @returns Array of entitled agent slugs
 */
export async function getOrgEntitlements(orgId: string): Promise<string[]> {
  return serverGet<string[]>(`/orgs/${orgId}/entitlements`);
}

/**
 * Update organization agent entitlements (super-admin only)
 *
 * @param orgId - Organization ID
 * @param agentSlugs - Array of agent slugs to entitle
 * @returns Updated array of entitled agent slugs
 */
export async function updateOrgEntitlements(
  orgId: string,
  agentSlugs: string[]
): Promise<string[]> {
  return serverPut<string[]>(`/orgs/${orgId}/entitlements`, { agent_slugs: agentSlugs });
}

/**
 * Check vb-server health
 *
 * @returns Health status
 */
export async function healthCheck(): Promise<{
  healthy: boolean;
  time?: string;
  version?: string;
  error?: string;
}> {
  try {
    const res = await serverGet<{
      status: string;
      timestamp: string;
      version: string;
    }>('/health');
    return {
      healthy: res.status === 'ok',
      time: res.timestamp,
      version: res.version
    };
  } catch (error) {
    return { healthy: false, error: String(error) };
  }
}
